// Shared by the browser app (src/) and the edge functions (Deno).
// Pure functions only — no imports — so both runtimes can load this file.

export const INTAKE_STEP_KEYS = ["you", "basics", "budget", "venue", "vision", "details", "working"];

// Required fields per step (quote-critical + routing only).
export const INTAKE_REQUIRED = {
  you: ["partner1Name", "partner2Name", "partner1Email", "partner1Phone", "homeCity"],
  basics: ["dateStatus", "location", "destination", "guests"],
  budget: ["budgetBand", "budgetFirm", "priorities"],
  venue: ["venueStatus", "venueType"],
  vision: ["tier", "howFarAlong", "must"],
  details: ["multiDay"],
  working: ["howHeard", "consent"]
};

export const BUDGET_BAND_MID = { "Under $25k": 20000, "$25k–50k": 37500, "$50k–100k": 75000, "$100k–200k": 150000, "$200k+": 250000 };

export const intakeFilled = v => Array.isArray(v) ? v.length > 0 : typeof v === "boolean" ? v : String(v == null ? "" : v).trim() !== "";

export function missingIntakeFields(form, stepKeys) {
  const keys = stepKeys || INTAKE_STEP_KEYS;
  const out = [];
  keys.forEach(k => (INTAKE_REQUIRED[k] || []).forEach(f => { if (!intakeFilled(form ? form[f] : undefined)) out.push(f); }));
  return out;
}

export const isEmail = s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());

const prettyDate = iso => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return months[m - 1] + " " + d + ", " + y;
};

export function newId(prefix) {
  return (prefix || "") + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

const arr = v => Array.isArray(v) ? v.map(x => String(x)) : [];

// Turns a submitted intake form into a client record (the same shape the dashboard has always used).
export function buildClientFromIntake(form, opts) {
  opts = opts || {};
  const t = k => String(form[k] == null ? "" : form[k]).trim();
  const p1 = t("partner1Name"), p2 = t("partner2Name");
  const rawTier = form.tier;
  const tier = (!rawTier || rawTier === "Help me choose") ? "Full Service" : rawTier;
  const budget = Number(form.budgetExact) || BUDGET_BAND_MID[form.budgetBand] || 0;
  const venue = t("venueName") || (form.venueStatus === "Need help finding one" ? "Venue — help needed" : (form.venueStatus === "Shortlisting" ? "Venue — shortlisting" : "TBD"));
  const lines = s => String(s || "").split("\n").map(x => x.trim()).filter(Boolean);
  const commas = s => String(s || "").split(",").map(x => x.trim()).filter(Boolean);
  return {
    id: opts.id || newId("c"),
    names: (p1.split(" ")[0] || p1) + " & " + (p2.split(" ")[0] || p2),
    full: p1 + " & " + p2,
    partners: [
      { name: p1, pronouns: t("partner1Pronouns"), email: t("partner1Email"), phone: t("partner1Phone") },
      { name: p2, pronouns: t("partner2Pronouns"), email: t("partner2Email"), phone: t("partner2Phone") }
    ],
    preferred: form.preferred || "Email",
    date: prettyDate(form.date) || (t("date") || "To be set"),
    venue,
    location: t("location"),
    guests: Number(form.guests) || 0,
    budget,
    tier,
    engaged: "To be confirmed",
    story: t("story") || "Shared via intake — details to follow.",
    colors: arr(form.colors).length ? arr(form.colors) : ["#c67139", "#7a8a5e", "#f5ead8", "#8c491a"],
    theme: t("theme") || "To be discussed",
    boards: lines(form.boards),
    must: commas(form.must),
    cant: commas(form.cant),
    dietary: t("dietary") || "None noted",
    accessibility: t("accessibility") || "None noted",
    family: t("family") || "None noted",
    requests: t("requests") || "None noted",
    source: opts.source || "dashboard",
    // Full structured intake record — the data foundation for AI quote generation.
    intake: {
      submittedAt: opts.now || new Date().toISOString(),
      you: {
        partner1: { name: p1, pronouns: t("partner1Pronouns"), email: t("partner1Email"), phone: t("partner1Phone") },
        partner2: { name: p2, pronouns: t("partner2Pronouns"), email: t("partner2Email"), phone: t("partner2Phone") },
        homeCity: t("homeCity"), preferredContact: form.preferred
      },
      basics: {
        dateStatus: form.dateStatus, date: t("date"), dateFlex: t("dateFlex"), dayOfWeek: form.dowPref,
        location: t("location"), destination: form.destination, destinationDetails: t("destinationDetails"),
        guests: Number(form.guests) || 0, guestCertainty: form.guestCertainty, partySize: Number(form.partySize) || 0
      },
      budget: {
        band: form.budgetBand, exact: Number(form.budgetExact) || null, firmness: form.budgetFirm,
        planningBudget: form.planningBudget, priorities: arr(form.priorities), willingToTrim: t("willingToTrim"), contributors: t("contributors")
      },
      venueVendors: {
        status: form.venueStatus, name: t("venueName"), sameSite: form.sameSite, venueType: form.venueType,
        outdoorNeeds: t("venueOutdoorNeeds"), vendorsBooked: arr(form.vendorsBooked), vendorsNeedHelp: arr(form.vendorsNeedHelp), officiantType: t("officiantType")
      },
      vision: {
        serviceTier: rawTier, howFarAlong: form.howFarAlong, designServices: form.designServices,
        tasksToOffload: arr(form.tasksToOffload), vibe: arr(form.vibe), formality: form.formality,
        dream: t("story"), inspiration: lines(form.boards),
        colors: arr(form.colors), theme: t("theme"),
        mustHaves: commas(form.must), dealbreakers: commas(form.cant), wowMoment: t("wowMoment")
      },
      details: {
        traditions: t("traditions"), fusion: form.fusion, extraEvents: arr(form.extraEvents), religiousReq: t("religiousReq"),
        familyDynamics: t("family"), languages: t("languages"), dietary: t("dietary"), accessibility: t("accessibility"),
        children: form.children, mealStyle: form.mealStyle, multiDay: form.multiDay,
        additionalEvents: t("additionalEvents"), transportLodging: t("transportLodging"), weatherRisk: t("weatherRisk"), siteNotes: t("siteNotes")
      },
      working: {
        meetingCadence: form.meetingCadence, involvement: form.involvement, otherDeciders: t("otherDeciders"),
        workedWithPlanner: form.workedWithPlanner, timelineToBook: form.timelineToBook,
        howHeard: form.howHeard, referrer: t("referrer"), notes: t("requests"), consent: !!form.consent
      }
    }
  };
}
